/**
 * Weekly Selections API service
 */

import { apiClient } from './client';
import type {
  GetWeeklySelectionsQuery,
  GetWeeklySelectionsResponse,
  BulkWeeklySelectionsRequest,
  BulkWeeklySelectionsResponse,
  GetAvailableMealsQuery,
  GetAvailableMealsResponse,
} from '@/types/api';
import type {
  FamilySummaryResponse,
  GetFamilySummaryQuery,
} from '@/types/weekly-selections';

/**
 * GET /api/weekly-selections
 * Get user's weekly meal selections
 */
export async function getWeeklySelections(
  params: GetWeeklySelectionsQuery
): Promise<GetWeeklySelectionsResponse> {
  const response = await apiClient.get<GetWeeklySelectionsResponse>('/weekly-selections', {
    params,
  });
  return response.data;
}

/**
 * POST /api/weekly-selections/bulk
 * Bulk create/update weekly meal selections
 * This replaces all selections for the user's week with the new set
 */
export async function bulkUpdateWeeklySelections(
  data: BulkWeeklySelectionsRequest
): Promise<BulkWeeklySelectionsResponse> {
  const response = await apiClient.post<BulkWeeklySelectionsResponse>(
    '/weekly-selections/bulk',
    data
  );
  return response.data;
}

/**
 * GET /api/weekly-selections/available-meals
 * Get available meals for selection, optionally with recommendation scores
 */
export async function getAvailableMeals(
  params: GetAvailableMealsQuery
): Promise<GetAvailableMealsResponse> {
  // Backend returns array of AvailableMealDto (with nested meal object)
  interface AvailableMealDto {
    meal: {
      id: number;
      name: string;
      tags: Array<{ id: number; tag: string; isHidden: boolean }>;
      createdAt: string;
      createdBy?: number;
      repeatIntervalWeeks?: number;
    };
    isSelected: boolean;
    selectionCount: number;
    userScore: number;
    recommendationScore: number;
  }

  const response = await apiClient.get<AvailableMealDto[]>('/weekly-selections/available-meals', {
    params,
  });

  // Transform to flattened structure for frontend use
  const meals = response.data.map(dto => ({
    ...dto.meal,
    repeatIntervalWeeks: dto.meal.repeatIntervalWeeks ?? 3,
    isSelected: dto.isSelected,
    selectionCount: dto.selectionCount,
    userScore: dto.userScore,
    recommendationScore: dto.recommendationScore,
    // Add userPreference for backward compatibility (derived from userScore)
    userPreference: dto.userScore > 0 ? {
      score: dto.userScore,
      voteCount: 1, // Backend doesn't provide this, use placeholder
    } : undefined,
  }));

  return {
    meals,
    total: meals.length,
  };
}

/**
 * GET /api/weekly-selections/family-summary
 * Get family meal selections summary for a week
 */
export async function getFamilySummary(
  params: GetFamilySummaryQuery
): Promise<FamilySummaryResponse> {
  const response = await apiClient.get<FamilySummaryResponse>(
    '/weekly-selections/family-summary',
    { params }
  );
  return response.data;
}
